import Group from '@polleverywhere/core/lib/models/group'
import Skeleton from '../../models/skeleton'
import ErrorModel from '../../models/error'
import Backbone from '@polleverywhere/backbone'
import constructActivity from '../../util/construct_activity'
import getParentMQA from '../../util/get_parent_mqa'
import computeActivityRoute from '../../util/compute_activity_route'

function updateRoute () {
  if (this.state.get('page') !== 'activities') {
    return
  }

  const appState = this.state.appStates.activities
  const route = computeActivityRoute.call(this, appState)

  if (route) {
    this.navigate(route.path, { trigger: false, replace: !route.track })
  }
}

/**
 * Updates URL when state changes. Also, update the state model
 * from the initial route.
 */
export default class Router extends Backbone.Router {
  get routes () {
    return {
      'activities/trash': () => {
        this.appState.changePage('trash')
        this.state.set({ page: 'activities' })
      },
      'activities/search(?term=:term)': (term) => {
        this.appState.changePage('search', {
          searchTerm: term || ''
        })
        this.state.set({ page: 'activities' })
      },
      'activities?group_id=:id': async (id) => {
        let group = new Group({ id })
        try {
          await group.fetch()
          let groups
          if (group.get('owner_id') !== this.state.get('user').id) {
            groups = this.state.appStates.activities.get('sharedGroups')
          } else {
            groups = this.state.get('user').groups
          }
          group = groups.add(group)
          this.appState.changePage('group', { group }, {
            scrollIntoView: true
          })
          this.state.set({ page: 'activities' })
        } catch (xhr) {
          group = new ErrorModel({ code: xhr.status, xhr })
          this.appState.changePage('group', { group })
          this.state.set({ page: 'activities' })
        }
      },
      activities: () => {
        this.appState.changePage('activities')
        this.state.set({ page: 'activities' })
      },
      // We need agave served under /office/index.html to direct all ALB traffic
      // from presenter.polleverywhere.com/office/index.html to singularity
      // because MSFT is very picky about domains and paths
      // Once we have phased out the web_presenter app entirely we can
      // change this around.
      'office/index.html': () => {
        this.appState.changePage('activities')
        this.state.set({ page: 'activities' })
      },
      ':type/:permalink(/:action)': async (type, permalink, action = null) => {
        // show skeleton activity
        this.appState.changePage('group', {
          activity: new Skeleton({ id: -1 }),
          group: new Group({ id: -2 })
        })

        this.state.set({ page: 'activities' })

        // start loading
        const groups = this.state.get('user').groups

        let activity = await constructActivity(type, permalink)

        // If the activity is part of an MQA, act on the parent MQA instead
        let selected
        const parentMQA = getParentMQA(activity)
        if (parentMQA) {
          selected = activity
          activity = await constructActivity(parentMQA.type, parentMQA.permalink)
        }

        // Not found
        if (activity instanceof ErrorModel) {
          const group = groups.get(-1)

          this.appState.changePage('group', {
            activity: activity,
            group
          })

          return
        }

        // Take care of manually fetching.
        // By default, the state model will assume this model has not been fully hydrated yet
        // and do another fetch which is unnecessary.
        const stateOpts = {
          fetch: false,
          selected
        }

        let group

        // Add the group only if we own the activity
        if (activity.presenter.id === this.state.get('user').id) {
          const groupId = activity.get('group_id') || -1

          // get or create the group
          group = groups.add({ id: groupId, owner_id: this.state.get('user').id })

          // remove the existing activity from the collection if it exists.
          // the ungrouped group is loaded async first so it might already contain is lightweight
          // version of this activity. we remove it instead of merging because the keys of
          // the activity have already been modified by the presenter instance and access to
          // the original json is almost impossible and error prone.
          group.activities.remove(activity)
          group.activities.add(activity)

          // fetch group if it hasn't been fetched yet
          if (!group.activities._lastAjax) {
            this.appState.fetchGroupActivities(group)
          }
        } else {
          // You don't own this poll so put it in a dummy group
          group = new Group({ id: -2, name: 'My activities' })
        }

        this.appState.changePage('group', {
          activity,
          group,
          pageAction: action
        }, stateOpts)
      }
    }
  }

  initialize (options = {}) {
    _.defaults(options, {
      updateURL: true
    })

    this.state = options.state
    this.appState = this.state.appStates.activities

    // Update route based on state changes
    if (!options.updateURL) {
      return
    }

    // Wire up page changes to the browser url
    this.listenTo(this.state, 'change:page', updateRoute.bind(this))

    // Wire up Activities state changes to the browser url
    this.listenTo(this.appState, 'change', updateRoute.bind(this))

    this.listenTo(this.appState, 'change:group', (state, group) => {
      // if we're viewing a group without an ID, update the path again once the id is available
      if (group && !group.has('id')) {
        this.listenToOnce(group, 'change:id', updateRoute.bind(this))
      }

      // Stop listening to the previous group
      const prevGroup = state.previous('group')
      if (prevGroup) {
        this.stopListening(prevGroup, 'change:id')
      }
    })

    // Update url on initial route.
    // We do this because this is the default route and we'll end up here before the router is even started.
    // Prior to backbone.history starting, `this.navigate` will not actually update the URL.
    // This ensures that the URL is properly updated if we're here before the router is started.
    this.listenToOnce(Backbone.history, 'route', updateRoute.bind(this))
  }
}
